import React, { useEffect, useState } from "react";
import axios from "axios";
import { Container, Table, Form, Button, Modal, Alert } from "react-bootstrap";

const BASE_URL = "https://madhusewingmachines.com/api/brands"; // Update this based on your backend

const BrandManagement = () => {
    const [brands, setBrands] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [newBrand, setNewBrand] = useState({ name: "", logo: null });
    const [editBrand, setEditBrand] = useState({ id: "", name: "", logo: null });
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // 📌 Fetch brands from API
    useEffect(() => {
        fetchBrands();
    }, []);

    const fetchBrands = async () => {
        try {
            const response = await axios.get(BASE_URL);
            setBrands(response.data.brands); // Adjusted based on API response format
        } catch (error) {
            console.error("Error fetching brands:", error);
            setError("Failed to load brands.");
        }
    };

    // 📌 Handle input changes (For Add & Edit)
    const handleChange = (e, type) => {
        if (type === "add") {
            if (e.target.name === "logo") {
                setNewBrand({ ...newBrand, logo: e.target.files[0] });
            } else {
                setNewBrand({ ...newBrand, [e.target.name]: e.target.value });
            }
        } else {
            if (e.target.name === "logo") {
                setEditBrand({ ...editBrand, logo: e.target.files[0] });
            } else {
                setEditBrand({ ...editBrand, [e.target.name]: e.target.value });
            }
        }
    };

    // 📌 Add New Brand
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newBrand.name || !newBrand.logo) {
            setError("Both brand name and logo are required!");
            return;
        }

        const formData = new FormData();
        formData.append("name", newBrand.name);
        formData.append("logo", newBrand.logo);

        try {
            await axios.post(BASE_URL, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setSuccess("Brand added successfully!");
            setError("");
            setShowModal(false);
            fetchBrands(); // Refresh brand list
        } catch (error) {
            setError("Failed to add brand. Try again.");
            console.error("Error adding brand:", error);
        }
    };

    // 📌 Open Edit Modal with selected brand data
    const openEditModal = (brand) => {
        setEditBrand({ id: brand._id, name: brand.name, logo: null });
        setShowEditModal(true);
    };

    // 📌 Edit Brand
    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!editBrand.name) {
            setError("Brand name is required!");
            return;
        }

        const formData = new FormData();
        formData.append("name", editBrand.name);
        if (editBrand.logo) {
            formData.append("logo", editBrand.logo);
        }

        try {
            await axios.put(`${BASE_URL}/${editBrand.id}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setSuccess("Brand updated successfully!");
            setError("");
            setShowEditModal(false);
            fetchBrands(); // Refresh brand list
        } catch (error) {
            setError("Failed to update brand. Try again.");
            console.error("Error updating brand:", error);
        }
    };

    // 📌 Delete Brand
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this brand?")) return;

        try {
            await axios.delete(`${BASE_URL}/${id}`);
            setSuccess("Brand deleted successfully!");
            fetchBrands(); // Refresh brand list
        } catch (error) {
            setError("Failed to delete brand.");
            console.error("Error deleting brand:", error);
        }
    };

    return (
        <Container className="mt-4">
            <h2 className="mb-3">🛍️ Brand Management</h2>

            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            {/* 📌 Add Brand Button */}
            <Button variant="primary" onClick={() => setShowModal(true)}>
                ➕ Add New Brand
            </Button>

            {/* 📌 Brand List Table */}
            <Table striped bordered hover responsive className="mt-3">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Brand Name</th>
                        <th>Logo</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {brands.length > 0 ? (
                        brands.map((brand, index) => (
                            <tr key={brand._id}>
                                <td>{index + 1}</td>
                                <td>{brand.name}</td>
                                <td>
                                    <img
                                        src={`https://madhusewingmachines.com/uploads/${brand.logo}`}
                                        alt={brand.name}
                                        width="50"
                                        height="50"
                                        className="rounded-circle"
                                    />
                                </td>
                                <td>
                                    <Button variant="warning" size="sm" onClick={() => openEditModal(brand)}>
                                        ✏️ Edit
                                    </Button>{" "}
                                    <Button variant="danger" size="sm" onClick={() => handleDelete(brand._id)}>
                                        ❌ Delete
                                    </Button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="4" className="text-center">
                                No brands found
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>

            {/* 📌 Add Brand Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Add New Brand</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Brand Name</Form.Label>
                            <Form.Control type="text" name="name" placeholder="Enter brand name" onChange={(e) => handleChange(e, "add")} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Brand Logo</Form.Label>
                            <Form.Control type="file" name="logo" onChange={(e) => handleChange(e, "add")} />
                        </Form.Group>
                        <Button variant="success" type="submit">✅ Add Brand</Button>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* 📌 Edit Brand Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Brand</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleEditSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Brand Name</Form.Label>
                            <Form.Control type="text" name="name" value={editBrand.name} onChange={(e) => handleChange(e, "edit")} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>New Logo (Optional)</Form.Label>
                            <Form.Control type="file" name="logo" onChange={(e) => handleChange(e, "edit")} />
                        </Form.Group>
                        <Button variant="success" type="submit">✅ Update Brand</Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default BrandManagement;
